<template>
  <div class="inputs border-radius-8 q-py-8 q-px-16">
    <AutocompleteGoogleMap
      ref="fromAutocompleteRef"
      :model-value="from"
      :placeholder="$t('placeholder.from')"
      class="flex-1 autocomplete-from"
      :rules="getRules('from')"
      :readonly="isPointTo"
      v-bind="inputAttrs"
      :error="!!getErrorMessage('from')"
      :error-message="getErrorMessage('from')"
      :borderless="!withToPoint"
      inaccurate-point-check
      right-icon-slot="after"
      @blur="onBlurAutocomplete('from')"
      @clear="emit('clear', 'from')"
      @focus="$emit('update:mapPinFor', 'from')"
      @click.prevent="isPointTo && $emit('choose-on-map', 'from')"
      @choose-on-map="$emit('choose-on-map', $event ? null : 'from')"
      @show-error="showErrorFrom = $event"
      @update:modelValue="onInput('from', $event)"
    >
      <template
        v-if="showDirectionsSymbol"
        #before
      >
        <div class="column relative-position self-center q-mt-4">
          <div class="route-point-icon fs-14">A</div>
          <Arrows
            v-if="withToPoint && !showErrorFrom"
            :is-round-trip="isRoundTrip"
          />
        </div>
      </template>
    </AutocompleteGoogleMap>
    <q-slide-transition>
      <div
        v-if="withToPoint"
        class="relative-position"
      >
        <div class="absolute full-width q-mt-none swap-block">
          <q-btn
            v-if="showSwapBtn"
            color="grey-12"
            size="10px"
            round
            unelevated
            @click="swap"
          >
            <q-icon
              name="img:common/icons/swap.svg"
              size="16px"
            />
          </q-btn>
        </div>
        <AutocompleteGoogleMap
          ref="toAutocompleteRef"
          :model-value="to"
          :placeholder="$t('placeholder.to')"
          class="flex-1 autocomplete-to"
          right-icon-slot="after"
          :rules="getRules('to')"
          :readonly="isPointFrom"
          :nearby-point="from.point"
          :error="!!getErrorMessage('to')"
          :error-message="getErrorMessage('to')"
          inaccurate-point-check
          v-bind="inputAttrs"
          borderless
          @blur="onBlurAutocomplete('to')"
          @clear="emit('clear', 'to')"
          @focus="$emit('update:mapPinFor', 'to')"
          @click.prevent="isPointFrom && $emit('choose-on-map', 'to')"
          @choose-on-map="$emit('choose-on-map', $event ? null : 'to')"
          @update:modelValue="onInput('to', $event)"
        >
          <template #before>
            <div class="route-point-icon fs-14 self-center q-mt-4">B</div>
          </template>
        </AutocompleteGoogleMap>
      </div>
    </q-slide-transition>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuasar } from 'quasar';

import AutocompleteGoogleMap from '~components/AutocompleteGoogleMap';
import Arrows from '~components/Arrows';
import { isInaccuratePoint } from '~common/utils/isInaccuratePoint';

const { t } = useI18n();
const $q = useQuasar();

const props = defineProps({
  from: {
    type: Object,
    required: true,
  },
  to: {
    type: Object,
    required: true,
  },
  lazyRules: {
    type: [String, Boolean],
    default: 'ondemand',
  },
  pointToChooseOnMap: {
    type: String,
    default: null,
  },
  mapPinFor: {
    type: String,
    default: null,
  },
  regionForbidden: {
    type: String,
    default: null,
  },
  showDirectionsSymbol: {
    type: Boolean,
    default: false,
  },
  isRoundTrip: {
    type: Boolean,
    default: false,
  },
  withToPoint: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'choose-on-map',
  'clear',
  'update:mapPinFor',
  'update:from',
  'update:to',
  'swap',
]);

const fromAutocompleteRef = ref(null);
const toAutocompleteRef = ref(null);

const showErrorFrom = ref(false);

const isPointFrom = computed(() => props.pointToChooseOnMap === 'from');
const isPointTo = computed(() => props.pointToChooseOnMap === 'to');

const inputAttrs = computed(() => ({
  hideDropdownIcon: true,
  lazyRules: props.lazyRules,
  reactiveRules: true,
  hideBottomSpace: true,
  withChooseOnMapBtn: $q.platform.is.mobile,
  useTextarea: true,
}));

const showSwapBtn = computed(() => props.withToPoint
  && !fromAutocompleteRef.value?.$refs.selectRef.hasError
  && !toAutocompleteRef.value?.$refs.selectRef.hasError
  && (props.from.name || props.to.name));

function onInput(direction, e) {
  emit(`update:${direction}`, e);
}

function getRules(direction) {
  return [
    () => !!(props.pointToChooseOnMap || props[direction].point) || t('error.required_address'),
    () => props.regionForbidden !== `${direction}_point_forbidden` || t('error.region.forbidden'),
    () => !isInaccuratePoint(props[direction]),
  ];
}

function getErrorMessage(direction) {
  if (props.regionForbidden === `${direction}_point_forbidden`) {
    return t('error.region.forbidden');
  }
  if (isInaccuratePoint(props[direction])) {
    return t('label.specify_exact_location');
  }
  return null;
}

function onBlurAutocomplete(direction) {
  if (direction === props.mapPinFor) {
    emit('update:mapPinFor', null);
  }
}

function swap() {
  emit('update:from', props.to);
  emit('update:to', props.from);
  emit('swap');
}

async function currentLocation(direction) {
  const autocomplete = direction === 'from' ? fromAutocompleteRef : toAutocompleteRef;
  await autocomplete.value?.currentLocation();
}

function focus(direction) {
  const autocomplete = direction === 'from' ? fromAutocompleteRef : toAutocompleteRef;
  autocomplete.value?.$refs.selectRef.focus();
}

function resetValidation() {
  fromAutocompleteRef.value?.$refs.selectRef.resetValidation();
  toAutocompleteRef.value?.$refs.selectRef.resetValidation();
}

function validate() {
  if (props.from.point) fromAutocompleteRef.value?.$refs.selectRef.validate();
  if (props.to.point) toAutocompleteRef.value?.$refs.selectRef.validate();
}

defineExpose({
  currentLocation,
  focus,
  resetValidation,
  validate,
});
</script>

<style scoped lang="scss">
.arrows {
  top: 31px;
  height: 26px !important;
}

.autocomplete-from, .autocomplete-to {
  :deep(.q-field__bottom) {
    width: 100%;
    .q-field__messages {
      & > div {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      @media screen and (max-width: $breakpoint-sm-max) {
        padding-left: 12px;
      }
    }
  }
}

.inputs {
  border: 1px solid #ddd;
}

@media screen and (max-width: $breakpoint-sm-max) {
  .autocomplete-from :deep(.q-field__messages) {
    margin-right: 20%;
  }
  .autocomplete-to :deep(.q-field--standard .q-field__control::after) {
    display: none;
  }
}

.route-point-icon {
  height: 32px;
  line-height: 32px;
  width: 32px;
}

.swap-block .q-btn {
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99;
}
</style>
